import React, { useContext } from 'react';
import { noop } from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Skeleton, SkeletonItem } from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useResponsiveStyles } from '@bingads-webui-campaign-react/common-hooks';
import { ValidatedBudgetInput } from '@bingads-webui-campaign-react/budget-component-v2';
import { HelpIcon } from '@bingads-webui-campaign-react/help';
import { biddingStrategyTypeId } from '@bingads-webui-campaign-react/constants';
import {
  BudgetTierTitles,
  BudgetDetailKeysByBidType,
  BudgetDetailsKeysText,
  BudgetDetailKeysByCampaignType,
  BudgetDetailKeys,
} from '../model/constants';

// Resolves the ordered set of metric column keys for a given bid strategy /
// campaign type, dropping CostPerConversion when a target CPA is set. Extracted
// so the default layout (Metrics) and the UC table layout share one source of
// truth for which columns render and in what order.
export const getBudgetDetailKeys = ({ bidType, campaignType, isTargetCpaSet }) => {
  let budgetDetailsKeys = BudgetDetailKeysByBidType[biddingStrategyTypeId[bidType]]
    || BudgetDetailKeysByCampaignType[campaignType]
    || BudgetDetailKeysByBidType.Default;

  // if we have isTargetCpaSet we want to hide CostPerConversion
  if (isTargetCpaSet) {
    budgetDetailsKeys = budgetDetailsKeys.filter(key => key !== BudgetDetailKeys.CostPerConversion);
  }

  return budgetDetailsKeys;
};

const getRadioStyles = () => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  radioTitle: {
    fontSize: '16px',
    fontWeight: '600',
  },
  flexBox: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  flexBoxWithTierDescription: {
    justifyContent: 'flex-start',
    columnGap: '24px',
    rowGap: '16px',
  },
  leftBox: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '5px',
  },
  leftBoxWithTierDescription: {
    flex: '0 0 180px',
    minWidth: '180px',
  },
  budgetDay: {
    fontSize: '14px',
    fontWeight: '600',
    borderRight: '1px solid rgba(224, 224, 224, 1)',
    verticalAlign: 'middle',
    paddingRight: '5px',
    height: 20,
    width: 'auto',
    display: 'inline-block',
  },
  budgetMonth: {
    fontSize: '14px',
    fontWeight: '600',
    height: 20,
    width: 'auto',
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
  },
  metric: {
    display: 'flex',
    flexDirection: 'column',
  },
  rightBox: {
    display: 'grid',
    'grid-template-columns': 'repeat(2, 1fr)',
    width: '250px',
    columnGap: '10px',
  },
  tierDescriptionColumn: {
    flexGrow: 0,
    flexShrink: 1,
    flexBasis: '240px',
    minWidth: '200px',
    maxWidth: '320px',
  },
  customBudgetControl: {
    width: '100%',
    minWidth: 0,
    '& .validated-budget-input-container': {
      width: '100%',
    },
    '& .budget-input-container': {
      width: '100%',
      minWidth: 0,
    },
    '& .budget-input-row': {
      width: '100%',
      minWidth: 0,
    },
    '& .budget-input': {
      maxWidth: '120px',
      minWidth: 0,
    },
    '& .budget-currency': {
      flexShrink: 0,
      whiteSpace: 'nowrap',
    },
  },
  badge: {
    borderRadius: '999px',
    textAlign: 'center',
    width: 81,
    height: 20,
    border: '1px solid rgba(159, 216, 159, 1)',
    color: 'rgba(14, 112, 14, 1)',
    fontSize: '10px',
    fontWeight: '600',
    background: 'rgba(241, 250, 241, 1)',
  },
  metricTitle: {
    fontSize: '10px',
    fontWeight: '400',
    color: 'rgba(97, 97, 97, 1)',
  },
  metricValue: {
    fontWeight: '600',
    fontSize: '14px',
  },
  budgetDisclaimer: {
    width: 'auto',
    fontSize: '10px',
    color: 'rgba(112, 112, 112, 1)',
    marginTop: '10px',
  },
  skeletionStyle: {
    height: 20,
    width: 60,
    display: 'inline-block',
    marginRight: '5px',
  },
});

export const Metrics = ({
  budgetDetails,
  isLoading,
  bidType,
  campaignType,
  isTargetCpaSet,
}) => {
  const i18n = useContext(I18nContext);

  const getStyles = useResponsiveStyles(getRadioStyles);

  const budgetDetailsKeys = getBudgetDetailKeys({ bidType, campaignType, isTargetCpaSet });

  return budgetDetailsKeys.map(key => (
    <div key={key} className={classNames(getStyles('column'))}>
      <div className={classNames(getStyles('metric'))}>
        <span className={classNames(getStyles('metricTitle'))}>
          {
            isLoading ?
              <Skeleton style={{ marginTop: '5px', marginBottom: '5px', width: '65px' }} appearance="translucent" animation="wave" aria-label={i18n.getString(_TL_('Loading'))}>
                <SkeletonItem />
              </Skeleton>
              :
              <>
                {i18n.getString(BudgetDetailsKeysText[key].title)}&nbsp;
                <HelpIcon topic={BudgetDetailsKeysText[key].popupId} ariaLabel={i18n.getString(_TL_('Learn more about {{item}}'), { item: BudgetDetailsKeysText[key].helpPopupTitle })} />
              </>
          }
        </span>
        <span className={classNames(getStyles('metricValue'))}>
          {
            isLoading ?
              <Skeleton style={{ width: '95px' }} width="100" appearance="translucent" animation="wave" aria-label={i18n.getString(_TL_('Loading'))}>
                <SkeletonItem />
              </Skeleton>
              : budgetDetails && budgetDetails[key].Range
          }
        </span>
      </div>
    </div>
  ));
};

export const BudgetItemLabel = ({
  tier,
  data,
  isLoading,
  currency,
  campaignType,
  bidType,
  focusAttributes,
  isTargetCpaSet,
  selectedTierDescription,
  hasTierDescriptionColumn,
}) => {
  const getStyles = useResponsiveStyles(getRadioStyles);

  const i18n = useContext(I18nContext);

  const {
    BudgetAmountDaily,
    BudgetAmountMonthly,
    BudgetDetails,
  } = data || {};

  return (
    <div
      {...focusAttributes}
      className={classNames(getStyles('root'), `budget-item-${tier}`)}
    >
      <div className={classNames(
        getStyles('flexBox'),
        hasTierDescriptionColumn && getStyles('flexBoxWithTierDescription')
      )}
      >
        <div className={classNames(
          getStyles('leftBox'),
          hasTierDescriptionColumn && getStyles('leftBoxWithTierDescription')
        )}
        >
          <span className={classNames(getStyles('radioTitle'))}>
            {i18n.getString(BudgetTierTitles[tier])}
          </span>
          <div>
            <span className={classNames(getStyles('budgetDay'))}>
              {isLoading ?
                <Skeleton className={classNames(getStyles('skeletionStyle'))} appearance="translucent" animation="wave" as="span">
                  <SkeletonItem className={classNames(getStyles('skeletionStyle'))} as="span" size={16} />
                </Skeleton>
                : `${i18n.formatCurrency(BudgetAmountDaily, currency)}/${i18n.getString(_TL_('day'))}`
              }
            </span>
            &nbsp;
            <span className={classNames(getStyles('budgetMonth'))}>
              {
                isLoading ?
                  <Skeleton className={classNames(getStyles('skeletionStyle'))} appearance="translucent" animation="wave" as="span">
                    <SkeletonItem className={classNames(getStyles('skeletionStyle'))} as="span" size={16} />
                  </Skeleton>
                  : `${i18n.formatCurrency(BudgetAmountMonthly, currency)}/${i18n.getString(_TL_('month'))}`
              }
            </span>
          </div>
        </div>
        <div className={classNames(getStyles('rightBox'))}>
          <Metrics budgetDetails={BudgetDetails} isLoading={isLoading} bidType={bidType} campaignType={campaignType} isTargetCpaSet={isTargetCpaSet} />
        </div>
        {hasTierDescriptionColumn && (
          <div className={classNames(getStyles('tierDescriptionColumn'))}>
            {selectedTierDescription}
          </div>
        )}
      </div>
    </div>
  );
};

BudgetItemLabel.propTypes = {
  tier: PropTypes.string.isRequired,
  data: PropTypes.shape({}).isRequired,
  isLoading: PropTypes.bool.isRequired,
  bidType: PropTypes.string.isRequired,
  campaignType: PropTypes.string.isRequired,
  currency: PropTypes.string,
  focusAttributes: PropTypes.shape({}).isRequired,
  isTargetCpaSet: PropTypes.bool,
  selectedTierDescription: PropTypes.node,
  hasTierDescriptionColumn: PropTypes.bool,
};

BudgetItemLabel.defaultProps = {
  currency: 'USD',
  isTargetCpaSet: false,
  selectedTierDescription: null,
  hasTierDescriptionColumn: false,
};

export const CustomBudgetLabel = ({
  isSelected,
  currency,
  onUpdateCustomBudget,
  customPerformanceData,
  hidePerformanceData,
  customBudgetResult,
  bidType,
  defaultBudget,
  minBudget,
  campaignType,
  maxBudget,
  showDropdown,
  onSelectCurrency,
  currencies,
  isTargetCpaSet,
  selectedTierDescription,
  hasTierDescriptionColumn,
}) => {
  const getStyles = useResponsiveStyles(getRadioStyles);

  const i18n = useContext(I18nContext);

  const {
    BudgetDetails,
    isLoading,
  } = customPerformanceData || {};

  const customBudgetInput = (
    <ValidatedBudgetInput
      validationProps={{
        onValidate: onUpdateCustomBudget,
        data: (customBudgetResult && customBudgetResult.data) || { budget: defaultBudget },
        initialValue: defaultBudget,
        alwaysShowError: (customBudgetResult && customBudgetResult.isValid === false),
      }}
      budgetKey="budget"
      currency={currency || 'USD'}
      label={i18n.getString(_TL_('Set the campaign\'s average daily budget'))}
      className="custom-budget-input"
      inputStyle={hasTierDescriptionColumn
        ? { width: '120px', maxWidth: '120px', flex: '0 0 120px' }
        : undefined}
      minBudget={minBudget}
      maxBudget={maxBudget}
      showDropdown={showDropdown}
      onSelectCurrency={onSelectCurrency}
      currencies={currencies}
    />
  );
  return (
    <div className={classNames(getStyles('root'), 'budget-item-CustomBudget')}>
      <div className={classNames(
        getStyles('flexBox'),
        hasTierDescriptionColumn && getStyles('flexBoxWithTierDescription')
      )}
      >
        <div className={classNames(
          getStyles('leftBox'),
          hasTierDescriptionColumn && getStyles('leftBoxWithTierDescription')
        )}
        >
          <span className={classNames(getStyles('radioTitle'))}> {i18n.getString(_TL_('Set your own budget'))} </span>
          {
            isSelected && (hasTierDescriptionColumn
              ? <div className={classNames(getStyles('customBudgetControl'))}>{customBudgetInput}</div>
              : customBudgetInput)
          }
        </div>
        <div className={classNames(getStyles('rightBox'))}>
          {
            // show two metrics in a column
            !hidePerformanceData && isSelected &&
            <Metrics budgetDetails={BudgetDetails} isLoading={isLoading} bidType={bidType} campaignType={campaignType} isTargetCpaSet={isTargetCpaSet} />
          }
        </div>
        {hasTierDescriptionColumn && (
          <div className={classNames(getStyles('tierDescriptionColumn'))}>
            {selectedTierDescription}
          </div>
        )}
      </div>
    </div>
  );
};


CustomBudgetLabel.propTypes = {
  isSelected: PropTypes.bool.isRequired,
  currency: PropTypes.string.isRequired,
  bidType: PropTypes.string.isRequired,
  onUpdateCustomBudget: PropTypes.func.isRequired,
  customPerformanceData: PropTypes.shape({
    BudgetDetails: PropTypes.shape({}),
    isLoading: PropTypes.bool,
  }),
  hidePerformanceData: PropTypes.bool,
  defaultBudget: PropTypes.number.isRequired,
  campaignType: PropTypes.string.isRequired,
  showDropdown: PropTypes.bool,
  onSelectCurrency: PropTypes.func,
  currencies: PropTypes.arrayOf(PropTypes.shape({})),
  isTargetCpaSet: PropTypes.bool,
  selectedTierDescription: PropTypes.node,
  hasTierDescriptionColumn: PropTypes.bool,
};

CustomBudgetLabel.defaultProps = {
  hidePerformanceData: false,
  customPerformanceData: {
    BudgetDetails: {},
    isLoading: false,
  },
  showDropdown: false,
  onSelectCurrency: noop,
  currencies: [],
  isTargetCpaSet: false,
  selectedTierDescription: null,
  hasTierDescriptionColumn: false,
};
